import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { BinAssignModal } from "@/components/harvest/BinAssignModal";
import { EXIT_STATUSES, isInStock, isTakenOut, type ExitStatus } from "@/lib/partStatus";
import { takeOutSparePart, undoTakeOut } from "@/lib/partTakeOut";
import { cn } from "@/lib/utils";
import { TAKEN_OUT_BUTTON_CLASS } from "@/components/inventory/PartStatusBlock";

interface Props {
  partId: string;
  partName: string;
  status: string;
  extractionStatus?: string | null;
  canUndo: boolean;
  /** Bin of the latest 'out' movement, with its address. */
  lastOutBin: { id: string; address: string } | null;
  /** FIX-298: sizing of the main action button inside the status block. */
  buttonClassName?: string;
}

export function PartTakeOutActions({ partId, partName, status, extractionStatus, canUndo, lastOutBin, buttonClassName }: Props) {
  const { t } = useTranslation("inventory");
  const { toast } = useToast();
  const qc = useQueryClient();
  const [takeOpen, setTakeOpen] = useState(false);
  const [undoOpen, setUndoOpen] = useState(false);
  const [binPickerOpen, setBinPickerOpen] = useState(false);
  const [reason, setReason] = useState<ExitStatus | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["spare_parts"] });
    qc.invalidateQueries({ queryKey: ["spare_parts_by_bin"] });
    qc.invalidateQueries({ queryKey: ["spare_part_movements", partId] });
  };

  const fail = (e: unknown) =>
    toast({
      variant: "destructive",
      title: t("takeOut.errorTitle"),
      description: t(`takeOut.errors.${(e as Error).message}`, { defaultValue: t("takeOut.errors.unknown") }),
    });

  const confirmTakeOut = async () => {
    if (!reason) return;
    setBusy(true);
    try {
      await takeOutSparePart(partId, reason, note);
      toast({ title: t("takeOut.success", { reason: t(`takeOut.reasons.${reason}`) }) });
      setTakeOpen(false);
      setReason(null);
      setNote("");
      refresh();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const doUndo = async (binId: string | null) => {
    setBusy(true);
    try {
      await undoTakeOut(partId, binId);
      toast({ title: t("takeOut.undoSuccess") });
      setUndoOpen(false);
      setBinPickerOpen(false);
      refresh();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const canTakeOut = isInStock(status) && extractionStatus === "extracted";

  return (
    <>
      {canTakeOut && (
        <Button className={buttonClassName} onClick={() => setTakeOpen(true)}>{t("takeOut.button")}</Button>
      )}
      {isTakenOut(status) && canUndo && (
        <Button variant="outline" className={cn(TAKEN_OUT_BUTTON_CLASS, buttonClassName)} onClick={() => setUndoOpen(true)}>{t("takeOut.undoButton")}</Button>
      )}

      <Dialog open={takeOpen} onOpenChange={(o) => !busy && setTakeOpen(o)}>
        <DialogContent className="max-w-md" onOpenAutoFocus={(event) => event.preventDefault()}>
          <DialogHeader>
            <DialogTitle>{t("takeOut.title")}</DialogTitle>
            <DialogDescription>{partName}</DialogDescription>
          </DialogHeader>
          <RadioGroup value={reason ?? ""} onValueChange={(v) => setReason(v as ExitStatus)} className="gap-2">
            {EXIT_STATUSES.map((r) => (
              <Label
                key={r}
                htmlFor={`reason-${r}`}
                className="flex items-center gap-3 min-h-12 px-4 rounded-lg border border-border cursor-pointer has-[:checked]:border-primary has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5 text-base font-normal"
              >
                <RadioGroupItem id={`reason-${r}`} value={r} />
                {t(`takeOut.reasons.${r}`)}
              </Label>
            ))}
          </RadioGroup>
          <div className="space-y-1.5">
            <Label htmlFor="takeout-note">{t("takeOut.noteLabel")}</Label>
            <Textarea id="takeout-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("takeOut.notePlaceholder")} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTakeOpen(false)} disabled={busy}>{t("takeOut.cancel")}</Button>
            <Button onClick={confirmTakeOut} disabled={!reason || busy}>{t("takeOut.confirm")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={undoOpen} onOpenChange={(o) => !busy && setUndoOpen(o)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("takeOut.undoTitle")}</DialogTitle>
            <DialogDescription>{partName}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            {lastOutBin && (
              <Button className="min-h-12" disabled={busy} onClick={() => doUndo(lastOutBin.id)}>
                {t("takeOut.putBackIn", { bin: lastOutBin.address })}
              </Button>
            )}
            <Button className="min-h-12" variant="outline" disabled={busy} onClick={() => setBinPickerOpen(true)}>
              {t("takeOut.chooseBin")}
            </Button>
            <Button className="min-h-12" variant="outline" disabled={busy} onClick={() => doUndo(null)}>
              {t("takeOut.noBin")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <BinAssignModal
        open={binPickerOpen}
        onOpenChange={setBinPickerOpen}
        selectedPartNames={[partName]}
        title={t("takeOut.chooseBin")}
        onAssign={(binId) => doUndo(binId)}
      />
    </>
  );
}
